// src/utils/navigator2026Export.ts

import jsPDF from 'jspdf';
import { format } from 'date-fns';

interface ApplicationData {
  _id: string;
  formType: string;
  formTitle: string;
  values: any;
  rawValues: any;
  status: string;
  submittedAt: string;
}

// Helper function to find value by key prefix
const findValue = (values: any, prefix: string) => {
  const key = Object.keys(values).find(k => k.startsWith(prefix));
  return key ? values[key] : null;
};

const formatValue = (value: any): string => {
  if (!value) return 'Not provided';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

export const exportNavigator2026ToPDF = (application: ApplicationData) => {
  const doc = new jsPDF();
  let yPos = 20;
  const margin = 20;
  const lineHeight = 6;
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const maxWidth = pageWidth - (margin * 2);

  const v = application.values;
  const applicantName = `${findValue(v, 'firstName') || ''} ${findValue(v, 'lastName') || ''}`.trim();

  // Helper to check if new page needed
  const checkNewPage = (additionalSpace = 15) => {
    if (yPos + additionalSpace > pageHeight - 20) {
      doc.addPage();
      yPos = 20;
      return true;
    }
    return false;
  };

  // Helper to add section header
  const addSectionHeader = (title: string) => {
    checkNewPage(20);
    yPos += 3; // Extra space before section
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(33, 150, 243); // Blue color
    doc.text(title, margin, yPos);
    yPos += lineHeight + 1;
    
    // Underline
    doc.setDrawColor(33, 150, 243);
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, margin + 60, yPos);
    yPos += lineHeight;
    
    doc.setTextColor(0, 0, 0); // Reset to black
  };

  // Helper to add field with label
  const addField = (label: string, content: string, indent: boolean = false) => {
    if (!content || content === 'Not provided') return;
    
    const xPos = indent ? margin + 5 : margin;
    const contentMaxWidth = maxWidth - (indent ? 5 : 0);
    
    checkNewPage(15);
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(label, xPos, yPos);
    yPos += lineHeight;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const lines = doc.splitTextToSize(content, contentMaxWidth);
    
    lines.forEach((line: string) => {
      checkNewPage(10);
      doc.text(line, xPos, yPos);
      yPos += lineHeight;
    });
    
    yPos += 2; // Small gap after field
  };

  // Helper to add text block (for long responses)
  const addTextBlock = (title: string, content: string) => {
    if (!content || content === 'Not provided') return;
    
    addSectionHeader(title);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(40, 40, 40);
    
    const lines = doc.splitTextToSize(content, maxWidth);
    lines.forEach((line: string) => {
      checkNewPage(10);
      doc.text(line, margin, yPos);
      yPos += lineHeight;
    });
    
    yPos += 3;
    doc.setTextColor(0, 0, 0);
  };

  // ===== HEADER =====
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Navigator 2026 Application', margin, yPos);
  yPos += 10;

  doc.setFontSize(16);
  doc.setTextColor(33, 150, 243);
  doc.text(applicantName, margin, yPos);
  doc.setTextColor(0, 0, 0);
  yPos += 10;

  // Submission details in smaller text
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(`Submitted: ${format(new Date(application.submittedAt), 'PPpp')}`, margin, yPos);
  yPos += 5;
  doc.text(`Status: ${application.status.toUpperCase()}`, margin, yPos);
  doc.setTextColor(0, 0, 0);
  yPos += 8;

  // Separator line
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 8;

  // ===== CONTACT INFORMATION =====
  addSectionHeader('Contact Information');
  addField('Email:', findValue(v, 'email') || 'Not provided', true);
  addField('Phone:', findValue(v, 'phone') || 'Not provided', true);
  
  const addressParts = [
    findValue(v, 'streetAddress1'),
    findValue(v, 'streetAddress2'),
    `${findValue(v, 'city')}, ${findValue(v, 'county')}`,
    findValue(v, 'postcode')
  ].filter(Boolean);
  
  if (addressParts.length > 0) {
    addField('Address:', addressParts.join('\n'), true);
  }

  // ===== ONLINE PRESENCE =====
  const link1 = findValue(v, 'field-1766062318797');
  const link2 = findValue(v, 'field-1766062356449');
  const link3 = findValue(v, 'field-1766062414839');
  
  if (link1 || link2 || link3) {
    addSectionHeader('Online Presence');
    if (link1) addField('Website/Social Link 1:', link1, true);
    if (link2) addField('Website/Social Link 2:', link2, true);
    if (link3) addField('Website/Social Link 3:', link3, true);
  }

  // ===== WORK SAMPLES =====
  const work1 = findValue(v, 'field-1766062474185');
  const work2 = findValue(v, 'field-1766062504736');
  const work3 = findValue(v, 'field-1766062529548');
  
  if (work1 || work2 || work3) {
    addSectionHeader('Work Samples');
    if (work1) addField('Work Sample 1 (Live Video):', work1, true);
    if (work2) addField('Work Sample 2:', work2, true);
    if (work3) addField('Work Sample 3:', work3, true);
  }

  // ===== LONG TEXT SECTIONS =====
  addTextBlock('About You', findValue(v, 'field-1766062588584'));
  addTextBlock('Why Navigator? (Value to Career)', findValue(v, 'field-1766062755596'));
  addTextBlock('Residency Plans', findValue(v, 'field-1766062851194'));

  // ===== ENGAGEMENT PREFERENCES =====
  const engagementPrefs = findValue(v, 'field-1766062954076');
  const otherEngagement = findValue(v, 'field-1766063184249');
  
  if (engagementPrefs || otherEngagement) {
    addSectionHeader('Engagement Preferences');
    
    if (engagementPrefs && Array.isArray(engagementPrefs)) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('Preferences:', margin + 5, yPos);
      yPos += lineHeight;
      
      doc.setFont('helvetica', 'normal');
      engagementPrefs.forEach((pref: string) => {
        checkNewPage(10);
        doc.text(`• ${pref}`, margin + 10, yPos);
        yPos += lineHeight;
      });
      yPos += 2;
    }
    
    if (otherEngagement) {
      addField('Other engagement ideas:', otherEngagement, true);
    }
  }

  // ===== LOCATION PREFERENCES =====
  const locationPrefs = findValue(v, 'field-1766063219475');
  const localInterest = findValue(v, 'field-1766063454068');
  
  if (locationPrefs || localInterest) {
    addSectionHeader('Location & Time Period Preferences');
    
    if (locationPrefs && Array.isArray(locationPrefs)) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('Locations:', margin + 5, yPos);
      yPos += lineHeight;
      
      doc.setFont('helvetica', 'normal');
      locationPrefs.forEach((loc: string) => {
        checkNewPage(10);
        const lines = doc.splitTextToSize(loc, maxWidth - 15);
        lines.forEach((line: string) => {
          doc.text(`• ${line}`, margin + 10, yPos);
          yPos += lineHeight;
        });
      });
      yPos += 2;
    }
    
    if (localInterest) {
      checkNewPage(20);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('Interest in Local Area:', margin + 5, yPos);
      yPos += lineHeight;
      
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(40, 40, 40);
      const lines = doc.splitTextToSize(localInterest, maxWidth - 5);
      lines.forEach((line: string) => {
        checkNewPage(10);
        doc.text(line, margin + 5, yPos);
        yPos += lineHeight;
      });
      doc.setTextColor(0, 0, 0);
      yPos += 3;
    }
  }

  // ===== ADDITIONAL NEEDS =====
  const additionalNeeds = findValue(v, 'field-1766063456747');
  if (additionalNeeds) {
    addTextBlock('Additional Needs / Caring Responsibilities', additionalNeeds);
  }

  // ===== FOOTER =====
  const pageCount = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
    
    doc.text(
      `Generated: ${format(new Date(), 'PP')}`,
      pageWidth - margin,
      pageHeight - 10,
      { align: 'right' }
    );
  }

  // Save the PDF
  const fileName = `Navigator2026_${applicantName.replace(/\s+/g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
  doc.save(fileName);
};

export const exportNavigator2026ToCSV = (application: ApplicationData) => {
  const v = application.values;
  const applicantName = `${findValue(v, 'firstName') || ''} ${findValue(v, 'lastName') || ''}`.trim();

  const rows: string[][] = [
    ['Field', 'Value'],
    ['Form Title', 'Navigator 2026 Application'],
    ['Applicant Name', applicantName],
    ['Submitted', format(new Date(application.submittedAt), 'PPpp')],
    ['Status', application.status.toUpperCase()],
    [''],
    ['CONTACT INFORMATION', ''],
    ['Email', findValue(v, 'email') || ''],
    ['Phone', findValue(v, 'phone') || ''],
    ['Street Address 1', findValue(v, 'streetAddress1') || ''],
    ['Street Address 2', findValue(v, 'streetAddress2') || ''],
    ['City', findValue(v, 'city') || ''],
    ['County', findValue(v, 'county') || ''],
    ['Postcode', findValue(v, 'postcode') || ''],
    [''],
    ['ONLINE PRESENCE', ''],
    ['Website/Social Link 1', findValue(v, 'field-1766062318797') || ''],
    ['Website/Social Link 2', findValue(v, 'field-1766062356449') || ''],
    ['Website/Social Link 3', findValue(v, 'field-1766062414839') || ''],
    [''],
    ['WORK SAMPLES', ''],
    ['Work Sample 1 (Live Video)', findValue(v, 'field-1766062474185') || ''],
    ['Work Sample 2', findValue(v, 'field-1766062504736') || ''],
    ['Work Sample 3', findValue(v, 'field-1766062529548') || ''],
    [''],
    ['About You', findValue(v, 'field-1766062588584') || ''],
    ['Why Navigator (Value to Career)', findValue(v, 'field-1766062755596') || ''],
    ['Residency Plans', findValue(v, 'field-1766062851194') || ''],
    [''],
    ['ENGAGEMENT PREFERENCES', ''],
    ['Selected Preferences', formatValue(findValue(v, 'field-1766062954076'))],
    ['Other Engagement Ideas', findValue(v, 'field-1766063184249') || ''],
    [''],
    ['LOCATION & TIME PREFERENCES', ''],
    ['Location Preferences', formatValue(findValue(v, 'field-1766063219475'))],
    ['Interest in Local Area', findValue(v, 'field-1766063454068') || ''],
    [''],
    ['Additional Needs / Caring Responsibilities', findValue(v, 'field-1766063456747') || '']
  ];

  const csvContent = rows.map(row => 
    row.map(cell => {
      const cellStr = String(cell || '');
      if (cellStr.includes(',') || cellStr.includes('\n') || cellStr.includes('"')) {
        return `"${cellStr.replace(/"/g, '""')}"`;
      }
      return cellStr;
    }).join(',')
  ).join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  const fileName = `Navigator2026_${applicantName.replace(/\s+/g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
  
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};